import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
/* Same hero gradient as Welcome / OperatorShell — avoids gray flash on login/logout + lazy route loads */
const loadingHeroBg =
  'linear-gradient(145deg, #041f24 0%, #0c4a5c 38%, #14464f 62%, #1f6d68 100%)';

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${loadingHeroBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  max-width: 400px;
  text-align: center;
`;

const LogoContainer = styled.div`
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
`;

const SpinnerContainer = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const SpinnerRing = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  animation: ${rotate} 1s linear infinite;
  
  &:nth-child(2) {
    width: 80%;
    height: 80%;
    top: 10%;
    left: 10%;
    border-top-color: rgba(255, 255, 255, 0.55);
    animation-duration: 1.5s;
    animation-direction: reverse;
  }
  
  &:nth-child(3) {
    width: 60%;
    height: 60%;
    top: 20%;
    left: 20%;
    border-top-color: rgba(200, 240, 230, 0.75);
    animation-duration: 2s;
  }
`;

const LoadingText = styled.h2`
  color: rgba(255, 255, 255, 0.96);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  animation: ${bounce} 2s ease-in-out infinite;
`;

const LoadingSubtext = styled.p`
  color: rgba(255, 255, 255, 0.72);
  font-size: 1rem;
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.5), rgba(200, 240, 230, 0.9), rgba(255, 255, 255, 0.6));
  border-radius: 2px;
  animation: ${shimmer} 2s ease-in-out infinite;
  background-size: 200px 100%;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  animation: ${bounce} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

const ModernLoadingScreen = ({ 
  message = null, 
  submessage = null, 
  showProgress = false,
  showDots = true 
}) => {
  const { language } = useLanguage();
  const t = translations[language].common;
  
  const displayMessage = message || t.loading;
  const displaySubmessage = submessage;

  return (
    <LoadingOverlay>
      <LoadingContainer>
        <LogoContainer>
          <Logo src="/images/QQ-Icon.svg" alt="Quisqueya Quest" />
        </LogoContainer>
        
        <SpinnerContainer>
          <SpinnerRing />
          <SpinnerRing />
          <SpinnerRing />
        </SpinnerContainer>
        
        <div>
          <LoadingText>{displayMessage}</LoadingText>
          <LoadingSubtext>{displaySubmessage}</LoadingSubtext>
        </div>
        
        {showProgress && (
          <ProgressBar>
            <ProgressFill />
          </ProgressBar>
        )}
        
        {showDots && (
          <LoadingDots>
            <Dot delay={0} />
            <Dot delay={0.2} />
            <Dot delay={0.4} />
          </LoadingDots>
        )}
      </LoadingContainer>
    </LoadingOverlay>
  );
};

export default ModernLoadingScreen;
